/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { TableCommandBarProps } from 'src/core/components/TableCommandBarProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { commandBarItemSelectionManager } from 'src/core/utils/commandBarItemSelectionManager';
import { QnaMakerSourceType } from 'src/qnaMaker/core/services/sources/qnaMakerSourcesSwaggers';

export type QnaAddSourceModalCommandBarProps = TableCommandBarProps<object> & {
    sourceKind: QnaMakerSourceType;
    isAddDisabled: boolean;
    onRenameSource: () => boolean | void;
    onDeleteSource: () => boolean | void;
    onAddSources: () => void;
};

const messages = defineMessages({
    addUrl: {
        id: 'qnaMaker.qnaAddSourceModalCommandBar.addUrl',
        defaultMessage: 'Add url'
    },
    addFile: {
        id: 'qnaMaker.qnaAddSourceModalCommandBar.addFile',
        defaultMessage: 'Add file'
    },
    editSource: {
        id: 'qnaMaker.qnaAddSourceModalCommandBar.editSource',
        defaultMessage: 'Edit'
    },
    deleteSource: {
        id: 'qnaMaker.qnaAddSourceModalCommandBar.deleteSource',
        defaultMessage: 'Delete'
    }
});

export const QnaAddSourceModalCommandBar = (props: QnaAddSourceModalCommandBarProps) => {
    const { selectedItems = [], sourceKind, isAddDisabled, onRenameSource, onDeleteSource, onAddSources } = props;
    const { localize } = useLocalization();

    const commands: ICommandBarItemProps[] = [
        {
            key: 'add',
            onClick: onAddSources,
            disabled: isAddDisabled,
            iconProps: { iconName: 'Add' },
            name: sourceKind === 'url' ? localize(messages.addUrl) : localize(messages.addFile)
        },
        {
            key: 'rename',
            onClick: onRenameSource,
            iconProps: { iconName: 'Rename' },
            name: localize(messages.editSource)
        },
        {
            key: 'delete',
            onClick: onDeleteSource,
            iconProps: { iconName: 'Delete' },
            name: localize(messages.deleteSource)
        }
    ];

    const items: ICommandBarItemProps[] = commandBarItemSelectionManager(
        commands,
        ['add'],
        ['rename', 'delete'],
        ['delete'],
        selectedItems.length
    );

    return <LuisCommandBar items={items} />;
};
